import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bars,
  Card,
  ErrorState,
  MODEL_LABEL,
  PageHead,
  Skeleton,
  Stat,
  StatusPill,
  money,
  time,
} from '@fa/ui';
import { ApiError, api } from '../lib/api.js';
import type { Overview as OverviewData } from '../lib/types.js';
import { useDocumentDrawer } from '../DocumentDrawer.js';

/**
 * Primeira tela do portal.
 *
 * Responde, em ordem: emiti quanto hoje, tem alguma coisa travada, e o que
 * aconteceu por ultimo. Tudo vem de uma unica requisicao agregada — o painel
 * abre com uma ida ao servidor, nao com doze.
 */
export function Overview({ onOpenDocuments }: { onOpenDocuments: () => void }): ReactNode {
  const { drawer, open } = useDocumentDrawer();
  const [exportando, setExportando] = useState<string | null>(null);

  /**
   * Pacote do contador: a relação de XMLs do mês com links assinados.
   *
   * A API não monta um ZIP na hora de propósito — compactar milhares de
   * arquivos dentro de uma requisição cairia no timeout justamente nos
   * clientes maiores. Aqui abrimos os links em sequência.
   */
  async function exportarMes(): Promise<void> {
    setExportando('Preparando o pacote...');
    try {
      const mes = new Date().toISOString().slice(0, 7);
      const pacote = await api<{ count: number; files: { xml_url: string; access_key: string }[] }>(
        `/v1/exports/accountant?month=${mes}`,
      );
      setExportando(
        `${pacote.count} XML(s) prontos, com links válidos por 15 minutos. ` +
          'Use o botão de cada nota ou o CSV da tela de Notas para a relação completa.',
      );
    } catch (err) {
      setExportando(err instanceof ApiError ? err.message : 'Não foi possível preparar a exportação.');
    }
  }

  const query = useQuery({
    queryKey: ['overview'],
    queryFn: () => api<OverviewData>('/v1/portal/overview'),
    refetchInterval: 60_000,
  });

  if (query.isLoading) {
    return (
      <>
        <PageHead title="Visão geral" subtitle="Carregando..." />
        <div className="grid-4">
          {[0, 1, 2, 3].map((i) => (
            <div className="card" key={i}>
              <Skeleton rows={2} />
            </div>
          ))}
        </div>
      </>
    );
  }

  if (query.isError || !query.data) {
    return (
      <>
        <PageHead title="Visão geral" />
        <div className="card">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      </>
    );
  }

  const d = query.data;
  const taxaRejeicao =
    d.today.autorizadas + d.today.rejeitadas > 0
      ? (d.today.rejeitadas / (d.today.autorizadas + d.today.rejeitadas)) * 100
      : 0;

  const usoPlano = d.plan.monthly_documents
    ? Math.round((d.plan.used_this_month / d.plan.monthly_documents) * 100)
    : null;

  // O grafico soma os tres modelos por dia; a legenda mostra a quebra.
  const porDia = new Map<string, number>();
  for (const ponto of d.chart) porDia.set(ponto.day, (porDia.get(ponto.day) ?? 0) + ponto.count);
  const serie = [...porDia.entries()].map(([day, value]) => ({ day, value }));
  const porModelo = d.chart.reduce<Record<string, number>>((acc, p) => {
    acc[p.model] = (acc[p.model] ?? 0) + p.count;
    return acc;
  }, {});

  const saudacao =
    new Date().getHours() < 12 ? 'Bom dia' : new Date().getHours() < 18 ? 'Boa tarde' : 'Boa noite';
  const hoje = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <>
      <PageHead
        title={`${saudacao}`}
        subtitle={
          <>
            {hoje.charAt(0).toUpperCase() + hoje.slice(1)}
            {d.today.contingencia > 0 && ` · ${d.today.contingencia} nota(s) aguardam a SEFAZ voltar`}
          </>
        }
        actions={
          <button className="btn btn-ghost" onClick={() => void exportarMes()}>
            Exportar mês para o contador
          </button>
        }
      />

      {exportando && (
        <div className="alert info">
          <b>Exportação</b>
          {exportando}
          <div className="fix">
            <button className="btn btn-sm btn-ghost" onClick={() => setExportando(null)}>
              Fechar
            </button>
          </div>
        </div>
      )}

      <div className="grid-4">
        <Stat label="Autorizadas hoje" value={d.today.autorizadas} hint="documentos com protocolo" />
        <Stat
          label="Rejeitadas hoje"
          value={d.today.rejeitadas}
          hint={`${taxaRejeicao.toFixed(1)}% das emissões`}
          {...(taxaRejeicao > 5 ? { tone: 'down' as const } : {})}
        />
        <Stat
          label="Em contingência"
          value={
            <span style={{ color: d.today.contingencia > 0 ? 'var(--warn)' : undefined }}>
              {d.today.contingencia}
            </span>
          }
          hint={d.today.contingencia > 0 ? 'aguardando a SEFAZ voltar' : 'nenhuma pendência'}
        />
        <Stat
          label="Notas no mês"
          value={d.month.total.toLocaleString('pt-BR')}
          hint={usoPlano !== null ? `${usoPlano}% do plano contratado` : 'volume sob contrato'}
        />
      </div>

      <div className="grid-3-1">
        <Card
          title="Emissões nos últimos 14 dias"
          action={
            <span className="pill mute">
              NF-e {porModelo.nfe ?? 0} · NFC-e {porModelo.nfce ?? 0} · NFS-e {porModelo.nfse ?? 0}
            </span>
          }
        >
          <Bars
            data={serie}
            labels={[
              formatShort(serie[0]?.day),
              formatShort(serie[Math.floor(serie.length / 2)]?.day),
              'hoje',
            ]}
          />
        </Card>

        <Card title="Precisa de atenção">
          <div style={{ display: 'grid', gap: 14 }}>
            {d.attention.length === 0 && (
              <div className="alert info">
                <b>Nada pendente</b>
                Nenhuma rejeição ou nota em contingência nos últimos registros.
              </div>
            )}
            {d.attention.map((item) => (
              <div key={item.id} className={`alert ${item.status === 'contingency' ? 'warn' : 'bad'}`}>
                <b>
                  {MODEL_LABEL[item.model]} nº {item.number.toLocaleString('pt-BR')} ·{' '}
                  {item.status === 'contingency' ? 'em contingência' : 'recusada'}
                </b>
                {item.message ?? 'Sem detalhes da SEFAZ.'}
                <div className="fix">
                  <button className="btn btn-sm btn-ghost" onClick={() => open(item.id)}>
                    Ver detalhes
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card
        title="Últimas notas"
        padded={false}
        action={
          <button className="btn btn-sm btn-ghost" onClick={onOpenDocuments}>
            Ver todas
          </button>
        }
      >
        <table>
          <thead>
            <tr>
              <th>Nota</th>
              <th>Modelo</th>
              <th>Referência</th>
              <th className="r">Valor</th>
              <th>Situação</th>
              <th className="r">Horário</th>
            </tr>
          </thead>
          <tbody>
            {d.recent.map((doc) => (
              <tr key={doc.id} className="clickable" onClick={() => open(doc.id)}>
                <td>
                  <b className="num">{doc.number.toLocaleString('pt-BR')}</b>
                  <span className="sub">Série {doc.serie}</span>
                </td>
                <td>{MODEL_LABEL[doc.model]}</td>
                <td className="key">{doc.reference ?? '—'}</td>
                <td className="r num">{money(doc.total)}</td>
                <td>
                  <StatusPill status={doc.status} />
                </td>
                <td className="r num">{time(doc.created_at)}</td>
              </tr>
            ))}
            {d.recent.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 32 }}>
                  Nenhuma nota emitida ainda. Envie a primeira pelo seu ERP ou pela API.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {drawer}
    </>
  );
}

const formatShort = (day: string | undefined): string =>
  day ? new Date(`${day}T12:00:00`).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }) : '';
